import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import JSZip from 'jszip';
import saveAs from 'save-as';
import { format} from 'date-fns'; 
// import axios from "axios";
import axiosInstance from "../../helpers/refreshTokenMiddleware";
import BASE_URL from "../../constants/Api";
// import MessageBox from './MessageBox';
import ConfirmPopup from './ConfirmPopup';
import Modal from '../Modal/Modal';
import { isChunkEligible, splitIntoQuarterWindowsBackward } from '../../helpers/chunkUtils';

const PhotoDownload = ({ getSearchConditions, setIsDownloading}) => {
    const DOWNLOAD_LIMIT = 500;
    const MASK_MSG_CLOSE = 0
    const MASK_MSG_OPEN_500ITEMS_OV = 1
    const MASK_MSG_OPEN_0ITEMS = 2
    const MASK_MSG_OPEN_NOPC = 4
    const MASK_YESNO_BTN = 0
    const MASK_OK_BTN = 1
    const [msgOpen, setMsgOpen] = useState(MASK_MSG_CLOSE);
    const bigCompanies = useSelector(state => state.login?.user?.big_companies || []);

    let zip = null; // instance of JSZip
    const processDataInBatches = async (data, batchSize) => {
        const totalBatches = Math.ceil(data.length / batchSize);
        const tasks = [];
        
        for (let i = 0; i < totalBatches; i++) {
            const batch = data.slice(i * batchSize, (i + 1) * batchSize);
            const task = processBatch(batch); // バッチずつを処理
            tasks.push(task);
        }
        
        await Promise.all(tasks); // 全てのタスクを実行
    }

    const processBatch = async (images) => {
        // Add Images to the zip file
        for (var i = 0; i < images.length; i++) {
            for (let index = 0; index < 3; index++) {
                try {
                    let response = null;
                    if ( (index === 0) && (images[i].presigned_image_url !== null) && (images[i].presigned_image_url !== '') ) {
                        response = await fetch(images[i].presigned_image_url);
                    } else if ( (index === 1) && (images[i].image_url !== null) && (images[i].image_url !== '') ) {
                        response = await fetch(images[i].image_url, {method: 'GET', credentials: 'include'});
                    } else {
                        if ( (images[i].presigned_image_url !== null) && (images[i].presigned_image_url !== '') ) {
                            response = await fetch(images[i].presigned_image_url);
                        } else if ( (images[i].image_url !== null) && (images[i].image_url !== '') ) {
                            response = await fetch(images[i].image_url, {method: 'GET', credentials: 'include'});
                        }
                    }
                    const blob = await response.blob();
                    // console.log(blob);
                    zip.file(images[i].filename, blob);
                    break;
                } catch (error) {
                    console.error(error);
                }
            }
        }
    }

    const fetchPage = async (page, lastKey, images, startDateOverride, endDateOverride) => {
        const baseUrl = BASE_URL + '/top/photo-download';
        let accessToken = localStorage.getItem('accessToken');

        const {
            companyId,
            startDate,
            endDate,
            facilityId,
            deviceId,
            gaugeId,
            failureId,
            status,
            searchComment,
            threshold
        } = getSearchConditions();


        const reqStart = startDateOverride ?? startDate;
        const reqEnd   = endDateOverride   ?? endDate;

        const requestBody = {
            company_id: companyId,
            failure_id: failureId,
            start_date: reqStart,
            end_date: reqEnd,
            page_group_number: null
        };

        if (lastKey !== undefined && lastKey !== null) {
            requestBody.key = lastKey;
        }

        const statusIndex = parseInt(status, 10);
        const searchCommentIndex = parseInt(searchComment, 10);
        const thresholdIndex = parseInt(threshold, 10);

        if (facilityId) requestBody.facility_id = facilityId;
        if (deviceId) requestBody.device_id = deviceId;
        if (gaugeId) requestBody.gauge_id = gaugeId;
        if (isNaN(statusIndex) === false && status !== '') requestBody.status = statusIndex;
        if (isNaN(searchCommentIndex) === false && searchComment !== '') requestBody.search_comment = searchCommentIndex;
        if (isNaN(thresholdIndex) === false && threshold !== '') requestBody.threshold = thresholdIndex;
        
        const config = {
            headers: {
                'Authorization': `Bearer ${accessToken}`,
                'Content-Type': 'application/json'
            }
        };

        try {
            // debugger
            const response = await axiosInstance.post(baseUrl, requestBody, config);

            // APIがnullを返した場合 → 即終了
            const respons = response?.data?.respons ?? null;
            if (respons === null) {
                return { results: images, stopAll: true, nextLastKey: null };
            }

            const ret = respons.images || [];
            const nextLastKey = respons.last_key;

            if ( (ret.length === 0) || (images.length + ret.length >= DOWNLOAD_LIMIT) || (nextLastKey === null)) {
                const results = images.concat(ret); // return images;
                if (results.length > DOWNLOAD_LIMIT) {
                    return { results: results.slice(0, DOWNLOAD_LIMIT), stopAll: false, nextLastKey: null };
                }
                return { results, stopAll: false, nextLastKey: null };
            } else {
                return fetchPage(page + 1, nextLastKey, images.concat(ret), reqStart, reqEnd);
            }
        } catch (error) {
            console.error('Error fetching images:', error);
            return { results: images, stopAll: false, nextLastKey: null };
        }
    };

     // 3か月ごとのウィンドウで順次呼び出す
    async function fetchAllImagesChunkedSequential() {
        const { startDate, endDate } = getSearchConditions();

        const windows = splitIntoQuarterWindowsBackward(startDate, endDate);
        let all = [];
        let stopAll = false;

        for (const [ws, we] of windows) {
            if (stopAll) break;
            const { results, stopAll: stopFlag } = await fetchPage(
                1,
                null,
                [],
                format(ws, "yyyy/MM/dd"),
                format(we, "yyyy/MM/dd")
            );
            all = all.concat(results);
            if (all.length >= DOWNLOAD_LIMIT) {
                all = all.slice(0, DOWNLOAD_LIMIT);
                break;
            }
            if (stopFlag) {
                stopAll = true;
                break;
            }
        }
        return all;
    }

    function fetchAllImages() {
        // チャンク条件を満たす場合 → チャンクモード
        const {
            companyId,
            startDate,
            endDate,
            status,
            searchComment,
            failureId
        } = getSearchConditions();

        if (isChunkEligible(companyId, startDate, endDate, searchComment, status, bigCompanies, failureId)) {
            return fetchAllImagesChunkedSequential();
        }

        // === フォールバック: 通常モード ===
        const fetchNonChunk = async (page, lastKey, images = []) => {
            const { results } = await fetchPage(page, lastKey, images, null, null);
            return results;
        };
        return fetchNonChunk(1, null, []);
    }

    const fetchImages = async () => {
        zip = new JSZip();
        setMsgOpen(MASK_MSG_CLOSE);
        setIsDownloading(true);
        fetchAllImages().then( async images => {
            if (images.length === 0) {
                setMsgOpen(MASK_MSG_OPEN_0ITEMS);
                setIsDownloading(false);
                return;
            }

            // バッチサイズ
            const batchSize = Math.ceil(images.length / 50);
            processDataInBatches(images, batchSize).then(async () => {
                // console.log('All batches processed');
                // Generate the zip file
                zip.generateAsync({type: "blob"}).then(function(content) {
                    saveAs(content, format(new Date(), 'yyyyMMddhhmmss')+'.zip');
                    zip = null;
                    setIsDownloading(false);
                });
            });
        });
    }

    const handleDownload = async () => {
        const {searchCount, deviceType} = getSearchConditions();
        if (deviceType !== 'desktop') {
            setMsgOpen(MASK_MSG_OPEN_NOPC);
            return;
        }
        if (searchCount > 0) {
            if (searchCount > DOWNLOAD_LIMIT) {
                setMsgOpen(MASK_MSG_OPEN_500ITEMS_OV);
            } else {
                fetchImages();
            }
        } else {
            setMsgOpen(MASK_MSG_OPEN_0ITEMS);
        }
    }

    return (
        <>
            <label className="mod-btn__round" id="photo_btn" onClick={handleDownload}>撮影データダウンロード</label>
            {/* <MessageBox
                title="撮影データダウンロード"
                message={`検索結果が${DOWNLOAD_LIMIT}件を超えています。<br>最新の${DOWNLOAD_LIMIT}件の画像データをダウンロードします。`}
                isOpen={(msgOpen & MASK_MSG_OPEN_500ITEMS_OV) === MASK_MSG_OPEN_500ITEMS_OV}
                onOK={fetchImages}
            />
            <MessageBox
                title="撮影データダウンロード"
                message="データ検索をしてください。"
                isOpen={(msgOpen & MASK_MSG_OPEN_0ITEMS) === MASK_MSG_OPEN_0ITEMS}
                onOK={()=> {setMsgOpen(MASK_MSG_CLOSE);}}
            /> */}
            {((msgOpen & MASK_MSG_OPEN_500ITEMS_OV) === MASK_MSG_OPEN_500ITEMS_OV) && (
                <ConfirmPopup   
                    handlePhotoShoot={fetchImages}                                     
                    onClose={() => setMsgOpen(MASK_MSG_CLOSE)}   
                    message={`検索結果が${DOWNLOAD_LIMIT}件を超えています。<br>最新の${DOWNLOAD_LIMIT}件の画像データをダウンロードします。`}
                    confirmButtons={MASK_YESNO_BTN}
                />
            )}
            {((msgOpen & MASK_MSG_OPEN_0ITEMS) === MASK_MSG_OPEN_0ITEMS) && (
                <ConfirmPopup   
                    handlePhotoShoot={() => setMsgOpen(MASK_MSG_CLOSE)}
                    onClose={() => setMsgOpen(MASK_MSG_CLOSE)}
                    message="データ検索をしてください。"
                    confirmButtons={MASK_OK_BTN}
                />
            )}
            {/* {((msgOpen & MASK_MSG_OPEN_NOPC) === MASK_MSG_OPEN_NOPC) && (
                <ConfirmPopup   
                    handlePhotoShoot={() => setMsgOpen(MASK_MSG_CLOSE)}
                    onClose={() => setMsgOpen(MASK_MSG_CLOSE)}
                    message="撮影データダウンロードはPCからご利用ください。"
                    confirmButtons={MASK_OK_BTN}
                />
            )} */}
            <Modal 
                isOpen={((msgOpen & MASK_MSG_OPEN_NOPC) === MASK_MSG_OPEN_NOPC)} 
                onClose={() => setMsgOpen(MASK_MSG_CLOSE)} 
                message="撮影データダウンロードはPCからご利用ください。" 
            />
        </>
    );
}

export default React.memo(PhotoDownload);